import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../services/dashboardService';
import { updateMetaTags } from '../utils/seo';
import UnifiedDashboard from './UnifiedDashboard';

const StatCard = ({ label, value, helper }) => (
  <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">{label}</p>
    <p className="text-3xl font-black text-slate-900 mt-3">{value}</p>
    {helper && <p className="text-xs text-slate-500 mt-1">{helper}</p>}
  </div>
);

const Section = ({ title, action, children }) => (
  <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-black tracking-[0.3em] text-slate-500 uppercase">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Badge = ({ children }) => (
  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 text-white">
    {children}
  </span>
);

const CustomerDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('customer'); // customer or business

  useEffect(() => {
    updateMetaTags({
      title: 'User Dashboard',
      description: 'Access your account features, track stats, and manage activity.',
      siteName: 'Ortus FMCG'
    });

    const loadDashboard = async () => {
      try {
        const payload = await dashboardService.fetch();
        console.log('Dashboard Payload:', payload);
        setData(payload);
        
        // Robust check for professional roles
        const userRoles = payload.roles || [];
        const businessRoles = ['investor', 'distributor', 'super_distributor', 'advertiser'];
        const hasProfessionalRole = userRoles.some(role => {
          const slug = typeof role === 'string' ? role : role.slug;
          return businessRoles.includes(slug);
        });

        // Auto-switch to business view if no customer orders but has business role
        if (hasProfessionalRole && (!payload.customer?.stats?.total_orders || payload.customer.stats.total_orders === 0)) {
          setView('business');
        }
      } catch (err) {
        console.error('Dashboard Error:', err);
        setError('Unable to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-red-500/20 border-t-red-500 rounded-full animate-spin"></div>
          <p className="text-slate-500 font-bold text-[10px] uppercase tracking-[0.3em]">Syncing Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
        <div className="bg-white border border-rose-100 text-rose-600 px-8 py-6 rounded-3xl font-bold shadow-xl shadow-rose-900/5 max-w-md text-center">
          <p className="mb-2">⚠️ {error || 'Dashboard unavailable.'}</p>
          <button onClick={() => window.location.reload()} className="text-xs text-slate-500 underline uppercase tracking-widest mt-4">Try Again</button>
        </div>
      </div>
    );
  }

  // Enhanced role check
  const userRoles = data.roles || [];
  const businessRoles = ['investor', 'distributor', 'super_distributor', 'advertiser'];
  const hasBusinessRole = userRoles.some(role => {
    const slug = typeof role === 'string' ? role : role.slug;
    return businessRoles.includes(slug);
  });

  // Business View Rendering
  if (view === 'business' && hasBusinessRole) {
    const businessData = data.investor || data.distributor || data.advertiser;
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-10 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex justify-center mb-8">
            <div className="bg-slate-900 p-1.5 rounded-2xl border border-slate-800 flex gap-1">
              <button 
                onClick={() => setView('customer')}
                className="px-6 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white transition"
              >
                Ecommerce View
              </button>
              <button 
                className="px-6 py-2 rounded-xl text-xs font-black bg-red-600 text-white shadow-lg"
              >
                Business Panel
              </button>
            </div>
          </div>
          <UnifiedDashboard data={businessData} />
        </div>
      </div>
    );
  }

  // Classic Customer Dashboard View
  const { user, stats, recent_orders = [], upcoming_deliveries = [], addresses = [], loyalty, profile_completion, alerts = [], payment_methods } = data.customer;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="max-w-6xl mx-auto space-y-8">
        {hasBusinessRole && (
          <div className="flex justify-center mb-4">
            <div className="bg-white p-1.5 rounded-2xl border border-slate-200 flex gap-1 shadow-sm">
              <button className="px-6 py-2 rounded-xl text-xs font-black bg-slate-900 text-white shadow-lg">
                Ecommerce View
              </button>
              <button 
                onClick={() => setView('business')}
                className="px-6 py-2 rounded-xl text-xs font-bold text-slate-500 hover:text-slate-900 transition"
              >
                Business Panel
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.4em] text-red-600">Dashboard</p>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mt-2">Welcome back, {user?.name}</h1>
            <p className="text-slate-500 mt-3">Track orders, deliveries, loyalty tier, and payment readiness.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/shop" className="px-5 py-3 rounded-2xl bg-slate-900 text-white font-semibold hover:bg-red-700">Continue Shopping</Link>
            <Link to="/profile" className="px-5 py-3 rounded-2xl border border-slate-200 text-slate-700 font-semibold hover:border-red-500">Update Profile</Link>
          </div>
        </div>

        {alerts.length > 0 && (
          <div className="grid md:grid-cols-2 gap-3">
            {alerts.map((alert, idx) => (
              <div key={idx} className="bg-amber-50 border border-amber-100 text-amber-700 rounded-2xl px-4 py-3 text-sm font-semibold">
                {alert.message}
              </div>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-4 gap-4">
          <StatCard label="Total Orders" value={stats?.total_orders ?? 0} />
          <StatCard label="Open Orders" value={stats?.open_orders ?? 0} helper="pending + in transit" />
          <StatCard label="Delivered" value={stats?.delivered_orders ?? 0} />
          <StatCard label="Lifetime Spend" value={`₹${(stats?.total_spent ?? 0).toLocaleString()}`} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Section title="Recent Orders" action={<Link to="/shop" className="text-xs font-semibold text-red-600">View all</Link>}>
            {recent_orders.length === 0 && <p className="text-sm text-slate-500">No orders yet.</p>}
            <div className="space-y-3">
              {recent_orders.map((order) => (
                <div key={order.id} className="flex items-center justify-between px-4 py-3 rounded-2xl bg-slate-50">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{order.order_number}</p>
                    <p className="text-xs text-slate-500">{new Date(order.placed_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-900">₹{order.total.toLocaleString()}</p>
                    <Badge>{order.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Upcoming Deliveries" action={<Link to="/shop" className="text-xs font-semibold text-red-600">Track</Link>}>
            {upcoming_deliveries.length === 0 && <p className="text-sm text-slate-500">No packages in transit.</p>}
            <div className="space-y-3">
              {upcoming_deliveries.map((delivery) => (
                <div key={delivery.id} className="px-4 py-3 rounded-2xl border border-slate-100">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-900">{delivery.order_number}</p>
                    <Badge>{delivery.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">ETA {delivery.delivery_eta ? new Date(delivery.delivery_eta).toLocaleDateString() : 'Updating'}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Loyalty" action={<span className="text-xs font-semibold text-slate-500">{profile_completion}% profile</span>}>
            <div className="bg-slate-900 rounded-3xl text-white p-6">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">Current Tier</p>
              <h3 className="text-3xl font-black mt-3">{loyalty?.tier}</h3>
              <p className="text-sm text-slate-200 mt-2">Lifetime value ₹{(loyalty?.lifetime_value ?? 0).toLocaleString()}</p>
              {loyalty?.next_tier && (
                <p className="text-xs text-slate-400 mt-4">{`₹${((loyalty.next_threshold ?? 0) - (loyalty.lifetime_value ?? 0)).toLocaleString()} to reach ${loyalty.next_tier}`}</p>
              )}
            </div>
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-500 mb-2">Payment Methods Enabled</p>
              <div className="flex flex-wrap gap-2">
                {payment_methods && Object.entries(payment_methods).map(([method, enabled]) => (
                  <span key={method} className={`px-3 py-1 rounded-full text-xs font-semibold ${enabled ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-400'}`}>
                    {method.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          </Section>
        </div>

        <Section title="Saved addresses" action={<Link to="/profile" className="text-xs font-semibold text-red-600">Manage</Link>}>
          {addresses.length === 0 && (
            <div className="bg-rose-50 border border-rose-100 rounded-2xl p-4 text-sm text-rose-600">
              Add a shipping address to speed up checkout.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {addresses.map((address) => (
              <div key={address.id} className="border border-slate-100 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-900">{address.full_name}</p>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">{address.type}</span>
                </div>
                <p className="text-sm text-slate-500">{address.address_line1}</p>
                {address.address_line2 && <p className="text-sm text-slate-500">{address.address_line2}</p>}
                <p className="text-sm text-slate-500">{address.city}, {address.state} {address.postal_code}</p>
                <p className="text-xs text-slate-400 mt-1">{address.country}{address.is_default ? ' • Default' : ''}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default CustomerDashboard;
